import React from "react";

const KPI_ACCENTS = {
  blue: {
    value: "text-[#2470B7] dark:text-sky-400",
    icon: "bg-blue-50 text-[#2470B7] dark:bg-sky-500/15 dark:text-sky-400",
    badge: "bg-blue-50 text-[#2470B7] dark:bg-sky-500/15 dark:text-sky-300",
  },
  orange: {
    value: "text-orange-500 dark:text-orange-400",
    icon: "bg-orange-50 text-orange-500 dark:bg-orange-500/15 dark:text-orange-400",
    badge: "bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300",
  },
  dark: {
    value: "text-gray-900 dark:text-slate-100",
    icon: "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-200",
    badge: "bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300",
  },
  green: {
    value: "text-green-600 dark:text-green-400",
    icon: "bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400",
    badge: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300",
  },
  red: {
    value: "text-red-600 dark:text-red-400",
    icon: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400",
    badge: "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300",
  },
  purple: {
    value: "text-purple-600 dark:text-purple-400",
    icon: "bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-400",
    badge: "bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  },
};

/**
 * Single-number KPI tile. Renders as a button when `onClick` is given.
 * While `loading`, the value and sub line are replaced by skeleton bars.
 */
function KpiCard({
  label,
  value,
  sub,
  badge,
  icon: Icon,
  color = "blue",
  onClick,
  loading = false,
}) {
  const accent = KPI_ACCENTS[color] || KPI_ACCENTS.dark;
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-busy={loading || undefined}
      className={`w-full rounded-[13px] border border-gray-200 bg-white p-4 text-left shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:shadow-none ${onClick ? "cursor-pointer transition-[box-shadow,border-color,background-color] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:hover:border-slate-600 dark:hover:bg-slate-700/50 dark:focus-visible:ring-sky-400/40" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="pt-1 text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-slate-400">
          {label}
        </p>
        {Icon && (
          <span
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${accent.icon}`}
          >
            <Icon size={16} strokeWidth={1.75} />
          </span>
        )}
      </div>
      {loading ? (
        <>
          <div className="mt-2 h-7 w-20 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
          <div className="mt-3 h-3 w-28 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
        </>
      ) : (
        <>
          <p className={`mt-1 text-[28px] font-bold leading-tight ${accent.value}`}>
            {value}
          </p>
          {(sub || badge) && (
            <div className="mt-2 flex min-h-[20px] flex-wrap items-center gap-2">
              {badge && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${accent.badge}`}
                >
                  {badge}
                </span>
              )}
              {sub && (
                <span className="text-xs text-gray-400 dark:text-slate-400">
                  {sub}
                </span>
              )}
            </div>
          )}
        </>
      )}
    </Tag>
  );
}

export default KpiCard;
